// Weekly intake form — mirrors /intake/weekly-intake-form.md verbatim.
// Q1 (name) is implicit; the backend stamps the session user.
//
// Branching: Q5=Yes reveals Q5a-Q5e. Q5b is capped at 40 client-side too.
// Q5b ≤ 0.5 step. Q6 multi-select; Q6a/b reveal conditionally.

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, type RosterRow, type Tier, type WeeklyInput, type WeeklySubmitted } from "../lib/api";

const TIER_LABELS: { v: Tier; label: string }[] = [
  { v: "Tier 1", label: "Tier 1 — Public (Claude.ai, ChatGPT, Copilot)" },
  { v: "Tier 2", label: "Tier 2 — Internal (Claude Enterprise, GitHub Copilot Business)" },
  { v: "Tier 3", label: "Tier 3 — Confidential (on-prem / VPN-only)" },
  { v: "None",   label: "None this week" },
];

const ACTIVITIES = [
  { v: "micro-course",      label: "Completed a micro-course (+10 XP)" },
  { v: "peer-endorsement",  label: "Got a peer endorsement (+20 XP)" },
  { v: "shared-prompt",     label: "Shared a reusable prompt to the prompt library (+30 XP)" },
  { v: "lunch-and-learn",   label: "Hosted a lunch-and-learn (+100 XP)" },
];

const initial: WeeklyInput = {
  q2_tiers: [],
  q3_days_used: 0,
  q4_top_use_case: "",
  q5_win: false,
  q6_xp_activities: [],
  q7_blocker: "",
};

export function Weekly() {
  const [v, setV] = useState<WeeklyInput>(initial);
  const [submitted, setSubmitted] = useState<WeeklySubmitted | null>(null);

  const roster = useQuery<RosterRow[]>({
    queryKey: ["roster", "active"],
    queryFn: async () => {
      try { return await api.roster.list(); } catch { return []; }
    },
  });

  const submit = useMutation({
    mutationFn: () => api.intake.submitWeekly(v),
    onSuccess: (data) => setSubmitted(data),
  });

  if (submitted) {
    return (
      <div className="space-y-3 max-w-xl">
        <h1 className="text-xl font-semibold">Thanks — submitted.</h1>
        <p className="text-sm text-slate-700">
          XP awarded this week: <b>{submitted.xp_awarded}</b><br />
          Week {submitted.week_iso}, {submitted.week_year}<br />
          {submitted.win_id ? <span>Win recorded; manager attestation pending.</span> : null}
        </p>
        <button
          className="px-3 py-1 rounded bg-slate-700 text-white text-sm"
          onClick={() => { setSubmitted(null); setV(initial); }}
        >
          Submit another
        </button>
      </div>
    );
  }

  const tierToggle = (t: Tier) => {
    setV((s) => ({
      ...s,
      q2_tiers: s.q2_tiers.includes(t)
        ? s.q2_tiers.filter((x) => x !== t)
        : [...s.q2_tiers, t],
    }));
  };

  const actToggle = (a: string) => {
    setV((s) => ({
      ...s,
      q6_xp_activities: s.q6_xp_activities.includes(a)
        ? s.q6_xp_activities.filter((x) => x !== a)
        : [...s.q6_xp_activities, a],
    }));
  };

  return (
    <form
      className="space-y-6 max-w-xl"
      onSubmit={(e) => { e.preventDefault(); submit.mutate(); }}
    >
      <header>
        <h1 className="text-xl font-semibold">Weekly AI Intake</h1>
        <p className="text-sm text-slate-600">
          2 minutes. A "nothing this week" is fine — we want signal, not theatre.
        </p>
      </header>

      <fieldset className="space-y-2">
        <legend className="font-medium">Q2. Which tier(s) did you use AI in this week? <span className="text-red-600">*</span></legend>
        {TIER_LABELS.map((t) => (
          <label key={t.v} className="block text-sm">
            <input
              type="checkbox"
              className="mr-2"
              checked={v.q2_tiers.includes(t.v)}
              onChange={() => tierToggle(t.v)}
            />
            {t.label}
          </label>
        ))}
      </fieldset>

      <fieldset>
        <legend className="font-medium">Q3. Days this week you used AI for work <span className="text-red-600">*</span></legend>
        <div className="flex gap-2 mt-2">
          {[0,1,2,3,4,5].map((n) => (
            <label key={n} className="flex flex-col items-center text-xs">
              <input
                type="radio"
                name="q3"
                value={n}
                checked={v.q3_days_used === n}
                onChange={() => setV({ ...v, q3_days_used: n })}
              />
              <span>{n}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend className="font-medium">Q4. Top use case this week <span className="text-red-600">*</span></legend>
        <p className="text-xs text-slate-600 mb-1">
          One sentence. e.g. "Drafted JD for senior backend role using Claude Enterprise."
        </p>
        <input
          className="w-full border rounded px-2 py-1 text-sm"
          value={v.q4_top_use_case}
          onChange={(e) => setV({ ...v, q4_top_use_case: e.target.value })}
          required
        />
      </fieldset>

      <fieldset>
        <legend className="font-medium">Q5. Did you have a win worth logging this week? <span className="text-red-600">*</span></legend>
        <div className="flex gap-4 mt-1 text-sm">
          <label><input type="radio" name="q5" checked={v.q5_win === true}  onChange={() => setV({ ...v, q5_win: true  })}/> Yes</label>
          <label><input type="radio" name="q5" checked={v.q5_win === false} onChange={() => setV({ ...v, q5_win: false })}/> No</label>
        </div>
      </fieldset>

      {v.q5_win ? (
        <div className="space-y-4 pl-4 border-l-2 border-blue-300">
          <fieldset>
            <legend className="font-medium">Q5a. One-sentence outcome <span className="text-red-600">*</span></legend>
            <input
              className="w-full border rounded px-2 py-1 text-sm"
              value={v.q5a_outcome ?? ""}
              onChange={(e) => setV({ ...v, q5a_outcome: e.target.value })}
              required
            />
          </fieldset>

          <fieldset>
            <legend className="font-medium">Q5b. Estimated hours saved <span className="text-red-600">*</span></legend>
            <p className="text-xs text-slate-600">Capped at 40 per submission.</p>
            <input
              type="number"
              step="0.5"
              min={0}
              max={40}
              className="w-32 border rounded px-2 py-1 text-sm"
              value={v.q5b_hours ?? ""}
              onChange={(e) => setV({ ...v, q5b_hours: Number(e.target.value) })}
              required
            />
          </fieldset>

          <fieldset>
            <legend className="font-medium">Q5c. Tier used for this win <span className="text-red-600">*</span></legend>
            <div className="flex gap-4 mt-1 text-sm">
              {(["Tier 1","Tier 2","Tier 3"] as Tier[]).map((t) => (
                <label key={t}>
                  <input
                    type="radio"
                    name="q5c"
                    checked={v.q5c_tier === t}
                    onChange={() => setV({ ...v, q5c_tier: t })}
                  /> {t}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="font-medium">Q5d. Is this a "high-impact" win? <span className="text-red-600">*</span></legend>
            <p className="text-xs text-slate-600">High-impact = saved &gt;10 hours, OR affected revenue, OR shipped to a customer. Triggers CEO + Strategist alert.</p>
            <div className="flex gap-4 mt-1 text-sm">
              <label><input type="radio" name="q5d" checked={v.q5d_high_impact === true}  onChange={() => setV({ ...v, q5d_high_impact: true  })}/> Yes</label>
              <label><input type="radio" name="q5d" checked={v.q5d_high_impact === false} onChange={() => setV({ ...v, q5d_high_impact: false })}/> No</label>
            </div>
          </fieldset>

          <fieldset>
            <legend className="font-medium">Q5e. Manager who can verify <span className="text-red-600">*</span></legend>
            <select
              className="w-full border rounded px-2 py-1 text-sm"
              value={v.q5e_manager_id ?? ""}
              onChange={(e) => setV({ ...v, q5e_manager_id: e.target.value })}
              required
            >
              <option value="">— pick —</option>
              {(roster.data ?? []).filter((r) => r.is_manager).map((r) => (
                <option key={r.id} value={r.id}>{r.name} ({r.function})</option>
              ))}
            </select>
            {roster.data && !roster.data.some((r) => r.is_manager) ? (
              <p className="text-xs text-amber-700">No managers in the roster yet. Ask an admin to flag one.</p>
            ) : null}
          </fieldset>
        </div>
      ) : null}

      <fieldset className="space-y-1">
        <legend className="font-medium">Q6. Which of these did you do this week?</legend>
        {ACTIVITIES.map((a) => (
          <label key={a.v} className="block text-sm">
            <input
              type="checkbox"
              className="mr-2"
              checked={v.q6_xp_activities.includes(a.v)}
              onChange={() => actToggle(a.v)}
            />
            {a.label}
          </label>
        ))}
        {v.q6_xp_activities.includes("peer-endorsement") ? (
          <div className="ml-6 mt-2">
            <label className="block text-xs text-slate-600">Q6a. Endorsement from</label>
            <input
              className="w-full border rounded px-2 py-1 text-sm"
              value={v.q6a_endorser ?? ""}
              onChange={(e) => setV({ ...v, q6a_endorser: e.target.value })}
            />
          </div>
        ) : null}
        {v.q6_xp_activities.includes("shared-prompt") ? (
          <div className="ml-6 mt-2">
            <label className="block text-xs text-slate-600">Q6b. Link to prompt PR / submission</label>
            <input
              className="w-full border rounded px-2 py-1 text-sm"
              value={v.q6b_prompt_link ?? ""}
              onChange={(e) => setV({ ...v, q6b_prompt_link: e.target.value })}
            />
          </div>
        ) : null}
      </fieldset>

      <fieldset>
        <legend className="font-medium">Q7. One thing that blocked you this week</legend>
        <p className="text-xs text-slate-600">Doesn't have to be AI-specific. The Strategist reads every response.</p>
        <textarea
          rows={3}
          className="w-full border rounded px-2 py-1 text-sm"
          value={v.q7_blocker ?? ""}
          onChange={(e) => setV({ ...v, q7_blocker: e.target.value })}
        />
      </fieldset>

      {submit.error ? (
        <p className="text-sm text-red-700">{(submit.error as Error).message}</p>
      ) : null}

      <button
        type="submit"
        disabled={submit.isPending}
        className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
      >
        {submit.isPending ? "Submitting…" : "Submit"}
      </button>
    </form>
  );
}
